import { UUID } from 'angular2-uuid';
import { Action } from 'redux';
import { CountryCode } from './enums/country-code.enum';

import { NOTIFICATION_LIVE, NOTIFICATION_ROLES } from './enums/notifications.enum';
import { UserTicketActionsEnum } from './enums/user-ticket-actions.enum';
import { HttpErrorResponse } from '@angular/common/http';

export class BaseModel {
    display?: string;
}

export class StopGroup {
    id: number;
    name: string;
    extra_information: {
        fileName: string;
        isDefault?: boolean;
        zones: number[];
    };
}

export class IntercityStop extends BaseModel {
    id: string;
    name: string;
    stop_area_id: string;
    stop_name: string;
    type?: string;
    groups?: StopGroup['id'][];
    stop_id: number;
    stop_lat: number;
    stop_lon: number;
    zone_id?: string;
}
export class Language extends BaseModel {
    code: string;
    name: string;
    default_language: boolean;
}
export class MatrixProductPrice extends BaseModel {
    origin_zone: number;
    destination_zone: number;
    origin_zone_name: string;
    destination_zone_name: string;
    includeLocationPrice: boolean;
    deviceChannelRequest: boolean;
    price: number;
    fare_rule_id: number;
    logo_keywords: string[];
    zones_reversed: boolean;
    isPriceMultiplyingDone: boolean;
    product_id: any;
}

export class Medium extends BaseModel {
    type: string;
    email?: string;
    card_number?: string;
    prefix?: string;
}

export class Notification extends BaseModel {
    uid: string;
    type: string;
    message: string;
    title?: string;
    translate: boolean;
    translations: any[];
    translationField: string;
    _role: string;
    ariaLive: string;
    constructor(
        type: string,
        message: string,
        title: string = null,
        translate = true,
        translations = [],
        translationField = null
    ) {
        super();
        this.uid = UUID.UUID();
        this.type = type;
        this.message = message;
        this.title = title;
        this.translate = translate;
        this.translations = translations;
        this.translationField = translationField;
        this._role = NOTIFICATION_ROLES.ALERT;
        this.ariaLive = NOTIFICATION_LIVE.ASSERTIVE;
    }

    get role() {
        return this._role;
    }

    set role(role: string) {
        this._role = role;
        if (role === NOTIFICATION_ROLES.STATUS) {
            this.ariaLive = NOTIFICATION_LIVE.POLITE;
        }
    }
}
export class PaymentMethod extends BaseModel {
    payment_id: number;
    payment_type: PaymentType;
    fee: number;
    fee_vat: number;
    name: string;
    banklink_account_id: number;
    code: string;
    translations: Translation[];
    not_allowed_reason?: string;
    is_corporate?: boolean;
    pin?: string;
    payment_tokens?: PaymentToken[];
    token_id?: number;
    order: number;
    display_tokens_first: boolean;
}

// paymentType wiki: https://ridango.atlassian.net/wiki/spaces/TEH/pages/92376689/payment+type+descr
export enum PaymentType {
    ADYEN = 'A',
    SWISH = 'H',
    WALLET = 'W',
    BANKLINK = 'B',
    INVOICE = 'I'
}

// https://ridango.atlassian.net/wiki/spaces/TEH/pages/23003352/payment+channel
export enum PaymentChannelCode {
    ADYEN = 'adyen',
    SWISH = 'swish',
    WALLET = 'wallet',
    PAYMENT_PORTAL = 'payments_portal',
    INVOICE = 'invoice',
    VPO = 'vpo'
}

export class PaymentToken extends BaseModel {
    cc_holder_name: string;
    cc_last_four_digits: string;
    cc_month: string;
    cc_type: string;
    cc_year: string;
    id: number;
}
export class AdyenSession extends BaseModel {
    session_id: string;
    session_data: string;
    client_key: string;
    sessionLoading: boolean;
    checkedPaymentStatus: any;
}
// Adyen result codes wiki: https://docs.adyen.com/online-payments/payment-result-codes
export enum AdyenResultStatus {
    AUTHORISED = 'Authorised',
    PENDING = 'Pending',
    RECEIVED = 'Received',
    REFUSED = 'Refused',
    CANCELLED = 'Cancelled'
}

export class ProductCategory extends BaseModel {
    id: number;
    keyword: string;
    type: string;
    name: string;
    description: string;
    translations: Translation[];
    order?: number;
    type_order?: number;
    categories: { category_id: number; category_order: number }[];
    extra_information?: {
        fileName: string;
        isDefault?: boolean;
        zones: number[];
    };
}

export enum ProductCategoryTypes {
    AREA = 'area',
    PRODUCT = 'product',
    TRAVELLER = 'traveller'
}

export class ProductGroup extends BaseModel {
    id: string;
    group_order: number;
    product_class?: string;
    product_type?: string;
    notification_mask?: number;
    notification_before_end?: number;
    units?: number;
    translations: Translation[];
    products: Product[];
    productIds: number[];
    price?: number;
}
export class ProductProperty extends BaseModel {
    code: string;
    name?: string;
    description?: string;
    value?: any;
}
export class Product extends BaseModel {
    card_type_mask?: number;
    group_id?: string;
    location_price?: number;
    logo_keywords?: string;
    max_amount?: string;
    min_amount?: string;
    name?: string;
    notification_before_end?: number;
    notification_mask?: number;
    order?: number;
    price?: number;
    product_amount?: number;
    product_category_ids?: any[];
    product_class?: string;
    product_id?: number;
    product_length?: any;
    product_properties?: ProductProperty[];
    product_type?: string;
    short_name?: string;
    translations?: Translation[];
    units?: number;
    visual_order?: number;
    area?: number;
    mobile_area?: Area;
}
export class RegionProperty extends BaseModel {
    code: string;
    value: string;
}
export class Region extends BaseModel {
    region_id: number;
    name: string;
    primary_color: string;
    settings: any; // old way, no point on creating an interface
    timezone: string;
    currency: string;
    region_properties: RegionProperty[];
    languages: Language[];
    country_code: CountryCode;
    transition_time_min: number;
    terms: any;
    reset_offline_tickets_list_time_min?: number;
}
// Wiki: https://ridango.atlassian.net/wiki/spaces/TEH/pages/1442042/shopping+cart
export class ShoppingCart extends BaseModel {
    id?: number;
    card_id?: string;
    created_at?: string;
    updated_at?: string;
    user_id?: number;
    shopping_cart_id?: number;
    total?: number;
    status?: string;
    org_id?: number;
    timestamp?: string | Date;
    a_date?: string;
    email?: string;
    type?: string;
    customer_id?: number;
    total_sum?: number;
    currency?: string;
    external_code?: string;
    region_id?: number;
    tickets?: any;
    docNums?: any[];
    jwt_token: string;
}
export enum CartStatus {
    IN_CART = 'S',
    LOCKED = 'L',
    VALID = 'Y'
}
export class TicketDetails extends BaseModel {
    origin_stop_name?: string;
    origin_stop_time_min?: number;
    destination_stop_name?: string;
    destination_stop_time_min?: number;
}

export class TicketNotification {}

/*
sync MobileTicket fields
    public Integer ticket_id;
    public Integer product_id;
    public Integer product_code;
    public Double price;
    public String name;
    public String name_et;
    public String name_en;
    public String name_ru;
    public String name_sv;
    public String name_da;
    public String name_kl;
    public String name_uk;
    public String short_name;
    public String short_name_et;
    public String short_name_en;
    public String short_name_ru;
    public String short_name_sv;
    public String short_name_da;
    public String short_name_kl;
    public String short_name_uk;
    public Date valid_from;
    public Date valid_to;
    public Date starting;
    public Date ending;
    public Long owner;
    public String valid;
    public String sms;
    public Long shopping_cart_id;
    public Long related_card_id;
    public Integer units;
    public String card_type;

    public String card_id;

    public Long start_zone_id;
    public Long end_zone_id;

    public String start_zone_name;
    public String end_zone_name;

    public Integer start_ext_zone_id;
    public Integer end_ext_zone_id;

    public String ticket_info;
    public String product_class;


    public Date timestamp;

    public String channel;
    public Integer product_units;

    public Integer region_mask;
    public Long doc_num;
    public String email;
    public Long fare_rule_id;
    public String seller_org_name;
    public String route_name;
    public String payment_channel;
    public String buyer_card_id;
    public String status;
    public Date to_a_date;
    public String is_active;
    public Integer notification_mask;
    public String mobile_phone;
    public String notification_email;
    public String route_short_name;
    public Integer product_length;
    public Integer region_id;
    public Integer pause_count;
    public Date paused_at;
    public Integer trip_id;
    public Boolean as_voucher;
    public String external_code;
    public String line_id;
    public Integer trip_departure_time_min;
    public Integer initial_units;
    public Date booklet_ending;
    public Integer max_units;

    public String owner_first_name;
    public String owner_last_name;
    public Date manual_validation_start_date;
    public Date manual_validation_end_date;
    public Long seat_id;
    public Integer location_order;

    public String origin_stop_name;
    public Integer origin_stop_time_min;
    public String origin_stop_type;
    public String destination_stop_name;
    public Integer destination_stop_time_min;
    public String destination_stop_type;

    public String reporting_info;

    public String mtb; // related mtb_manifest.mtb value

*/

export class Ticket extends BaseModel {
    card_id?: string;
    buyer_card_id?: string;
    card_type?: string;
    currency?: string;
    customer_id?: number;
    doc_num?: number;
    email?: string;
    ending?: string | Date;
    price?: number | { amount?: number; currency?: string | number; minAmount?: number; maxAmount?: number };
    farePrice?: { amount?: number; currency?: string | number; minAmount?: number; maxAmount?: number };
    base_price?: number;
    product_id?: number;
    productUid?: string;
    related_card_id?: number;
    shopping_cart_id?: number;
    starting?: string | Date;
    status?: string;
    timestamp?: string | Date;
    uid?: string;
    visual_card_id?: string;
    visual_ending?: Date;
    jwt_token?: string;

    destination_stop_area_id?: string;
    destination_stop_object?: IntercityStop;
    end_zone_id?: number;
    line_id?: string;
    origin_stop_area_id?: string;
    origin_stop_object?: IntercityStop;
    product_price_object?: MatrixProductPrice;
    route_short_name?: string;
    start_zone_id?: number;
    ticket_details?: TicketDetails;
    trip_departure_time_min?: number;
    trip_direction?: string;
    trip_id?: number;
    trip_object?: Trip;

    area?: Area;
    medium_object?: Medium;
    mediums?: Medium[];
    product?: Product;
    product_object?: Product;
    product_type?: string;
    productCategory?: ProductCategory; // type 'product' usually for length grouping
    translations?: Translation[];
    travellerCategory?: ProductCategory;

    buyer_channel?: string;
    buyer?: number;
    contractor_org_id?: number;
    issuer_org_id?: number;
    price_vat?: number;
    seller_dept_id?: number;
    seller_org_id?: number;
    seller_seller_id?: number;
    ticket_info?: string;
    ticket_notifications?: TicketNotification[];

    travelCardsIds?: number[];
    ticketsGroupedProductIds?: number[];
    mediumNums?: string[];
    emails?: string[];
    docNums?: number[];

    actions?: UserTicketAction[];
    receiptDownloadable?: boolean;
    start_stop?: string;
    end_stop?: string;
    notifications?: any;
    start_zone_name?: string;
    end_zone_name?: string;
    start_zone?: any;
    end_zone?: any;
    departure_time?: any;
    departure_time_min?: any;
    arrival_time_min?: any;

    name?: string;

    units?: number;
    initial_units?: number;

    pause_count?: number;
    paused_at?: string;
    renewable?: boolean;
    refund_code?: string;

    trip_messages?: any[];

    bought_with_subscription?: boolean;
    userProfiles?: UserProfile[];
    dynamicProductParameters?: SelectedDynamicProductParameters;
    customerAccountId?: number | string;
    // TODO: notifications typing
}

export interface Translatable {
    translations: Translation[];
}

export type KeyValueTranslation = {
    [key: string]: string;
};

interface UserProfile {
    companionProfiles: CompanionProfile[];
    group: boolean;
}

interface CompanionProfile {
    userProfile: {
        nameTranslations: Translatable | { KeyValueTranslation };
    };
    dynamicProductParameters: CompanionDynamicProductParameter[];
}

interface CompanionDynamicProductParameter {
    code: string;
}

interface SelectedDynamicProductParameters {
    [key: string]: number | string;
}

// TODO: should extend Ticket model, but ticket model right now is bloated aswell, so must fix that first
export class RefundableTicket extends BaseModel {
    card_type: string;
    price: number;
    units: number;
    starting: string;
    ending: string;
    timestamp: string;
    doc_num: string;
    seller_org_name: string;
    route_name: string;
    payment_channel: string;
    email?: string;
    line_id: string;
    trip_departure_time_min: number;
    status: string;
    card_id: string;
    ticket_info: string;
    is_valid: boolean;
    product: Product;
    area: Area;
    ticket_details: TicketDetails;
    refundable_status: string;
    seat_name: string;
}

export interface RefundMethodInterface {
    type: string;
    name: string;
    getPayload();
}
export class RefundMethod extends BaseModel implements RefundMethodInterface {
    type: string;
    name: string;
    constructor(type: string, name: string) {
        super();
        this.type = type;
        this.name = name;
        this.display = name;
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getPayload() {
        console.log('getPayload not implemented');
    }
}
export class RefundMethodWallet extends RefundMethod {
    card_id: string;
    getPayload() {
        return {
            card_id: this.card_id
        };
    }
}

export class RefundMethodBanklink extends RefundMethod {
    iban: string;
    full_name: string;
    email: string;
    phone: string;
    getPayload() {
        return {
            iban: this.iban,
            full_name: this.full_name,
            email: this.email,
            phone: this.phone
        };
    }
}

// starting to differentiate between SYNC ticket object and locally used ticket (not complete, all fields that are used locally should be ported here)
export class LocalWebTicket extends Ticket {
    related_card_object?: RelatedCard;
    actions?: UserTicketAction[];
    starting_formatted?: string;
    seatName?: string;
    seat_name?: string;
    multi_train?: boolean;
    train_name?: string;
    name?: string;
    amount?: number;
    wifi_key?: string;
    booklet_ending?: any;
    company_id?: any;
    channel?: string;
}

export class GroupedTickets {
    tickets: LocalWebTicket[] = [];
    total: number;
    origin_stop_name?: string;
    destination_stop_name?: string;
    origin_zone_name?: string;
    destination_zone_name?: string;
    origin_stop_time_min: number;
    destination_stop_time_min: number;
    starting: string;
    starting_formatted?: string;
    ending: string;
    actions: UserTicketAction[];
}

export class FailedCartTicket {
    card_type: any;
    channel: string;
    company_id: number;
    product_id: number;
    related_card_id: number;
    ticket_creation_error: string;
}

export class UserTicketAction {
    private _type: UserTicketActionsEnum;
    private _class: string;
    private _title: string;
    private _description: string;
    constructor(type: UserTicketActionsEnum) {
        this._type = type;
        switch (type) {
            case UserTicketActionsEnum.QR:
                this._class = 'qr';
                this._title = 'ACTION_SEE_QR_TITLE';
                this._description = 'ACTION_SEE_QR_DESCRIPTION';
                return;
            case UserTicketActionsEnum.DELETE:
                this._class = 'delete';
                this._title = 'ACTION_DELETE_TITLE';
                this._description = 'ACTION_DELETE_DESCRIPTION';
                return;
            case UserTicketActionsEnum.REMOVE:
                this._class = 'remove';
                this._title = 'ACTION_REMOVE_TITLE';
                this._description = 'ACTION_REMOVE_DESCRIPTION';
                return;
            case UserTicketActionsEnum.REFUND:
                this._class = 'refund';
                this._title = 'ACTION_REFUND_TITLE';
                this._description = 'ACTION_REFUND_DESCRIPTION';
                return;
            case UserTicketActionsEnum.BUY_AGAIN:
                this._title = 'ACTION_BUY_AGAIN_TITLE';
                this._description = 'ACTION_BUY_AGAIN_DESCRIPTION';
                return;
            case UserTicketActionsEnum.PAUSE:
                this._title = 'ACTION_PAUSE_TITLE';
                this._description = 'ACTION_PAUSE_DESCRIPTION';
                return;
            case UserTicketActionsEnum.REACTIVATE:
                this._title = 'ACTION_REACTIVATE_TITLE';
                this._description = 'ACTION_REACTIVATE_DESCRIPTION';
                return;
            case UserTicketActionsEnum.RENEW:
                this._title = 'ACTION_RENEW_TITLE';
                this._description = 'ACTION_RENEW_DESCRIPTION';
                return;
        }
    }
    get type() {
        return this._type;
    }
    get class() {
        return this._class;
    }
    get title() {
        return this._title;
    }
    get description() {
        return this._description;
    }
}

export class UserTicketActionCarrier {
    constructor(
        public ticket: LocalWebTicket,
        public action: UserTicketAction
    ) {}
}

export class Translation extends BaseModel {
    lang: string;
    field: string;
    translation: string;
}

export class Trip extends BaseModel {}

export class Area extends BaseModel {
    translations: Translation[];
    id: number;
    area_id: number;
    descr: string;
}

export class RelatedCard extends BaseModel {
    id: number;
    created_at?: string;
    updated_at?: string;
    customer_id?: number;
    name?: string;
    is_card?: string;
    email?: string;
    card_type?: string;
    authenticated?: string;
    card_nr?: string;
    layout?: string;
    groups?: string;
    wallet_balance?: number; // balance in cents
    has_related_card_pin?: boolean;
    blocked?: string;
    emv_card_info?: EmvCardInfo;
    blocking_allowed?: boolean;
}
class EmvCardInfo {
    scheme: string;
    pan_last_digits?: string;
    label: string;
    fundingToken: string;
}
export class NotificationSetting extends BaseModel {
    channels: NotificationChannel;
}

export class NotificationChannel extends BaseModel {
    judu: boolean;
    sms: boolean;
    email: boolean;
    pushNotification: boolean;
}

export class CardGroup extends BaseModel {
    id: number;
    group_id: number | '';
    name: string;
    translations: Translation[];
}

export class CardStatus extends BaseModel {
    registered: boolean;
    registered_to_current_customer: boolean;
}

export class Company extends BaseModel {
    id: number;
    created_at?: string;
    updated_at?: string;
    name: string;
    regnr: string;
    vatnr?: string;
    address_id?: number;
    address_line_1?: string;
    contact_person_name?: string;
    country?: string;
    email?: string;
    locality?: string;
    phone?: string;
    postal_code?: string;
    parentCompany?: {
        id: number;
        name: string;
    };
}

export class PowerUser extends BaseModel {
    id: number;
    companyId: number;
    customerEmail: string;
    accessType: AccessType;
}

export class CardBalance extends BaseModel {
    card_id?: number;
    balance?: number;
    credit_limit?: number;
    card_type?: string;
    currency?: string;
}

/* Interfaces */

export interface RidangoReduxAction<T = any> extends Action {
    type: string;
    payload?: T;
}

export interface TicketMediumInterface {
    type: string;
    name: string;
}

export class TicketMedium extends BaseModel implements TicketMediumInterface {
    type: string;
    name: string;
    constructor(type: string, name: string) {
        super();
        this.type = type;
        this.name = name;
        this.display = name;
    }
}

export interface Settings {
    API_BASE_URL: string;
    APP_PROPERTY: AppPropertyObject;
}

export interface AppPropertyObject {
    [id: string]: AppProperty;
}

export interface AppProperty {
    assetsDir: string;
    disableTicket: boolean;
    hasDisclaimer: boolean;
    hostApp?: string;
    currency?: string;
    purchaseTicketsTableColumns?: string[];
    titleVisible?: boolean;
    dateFormat?: string;
    useNewCardTokenForm?: boolean;
}

export interface WidgetOptions {
    locale: string;
    version: string | number;
}

export interface WidgetOptionsForEmailRegister extends WidgetOptions {
    showRequiredText: boolean;
    fullNameRequired?: boolean;
}

export interface RegisterWithEmailDTO {
    email: string;
    password: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    personalCode?: number;
    terms?: any;
    language?: string;
    callbackUrl?: string;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface SetProfileInformationDTO {
    name?: string;
    phone?: number | string;
    adr_city?: string;
    adr_street_1?: string;
    adr_zip?: number;
}

export interface SetCustomerProfileInformation {
    firstName?: string;
    lastName?: string;
    phoneNumber?: number;
    phone?: Phone;
    lang?: string;
}

export interface Phone {
    code: string;
    number: number;
}

export interface ResetCompleteEvent {
    loginSuccessful: boolean;
    authAccessToken: string;
}

export interface ChangePasswordDTO {
    email: string;
    password: number;
    newPassword: string;
    oauthClientId: string;
}

export interface RelatedCardDTO {
    card_nr: number;
    card_pin: number;
    name: string;
    card_type: string;
    region_id: number;
}

export interface RedirectSSOUrlResponse {
    redirectUrl: string;
    authenticated: boolean;
}

export interface OidcAuthStartResponse {
    redirectUrl: string;
}

export interface OidcAuthResponse {
    accessToken: string;
    refreshToken: string;
    tokenType: string;
}

export interface OidcAuthCustomerDeviceRequest {
    transportCustomerId?: number;
    lastSessionId?: string;
    deviceId?: string;
    model?: string;
    osVersion?: string;
    lastLoginAt?: string;
}

export interface OidcAuthStartRequest {
    clientId: string;
    redirectUri: string;
    lang?: string;
    channel: string;
    customerDevice?: OidcAuthCustomerDeviceRequest;
}

export interface OidcSignupCompleteRequest {
    phoneNumber: string;
    personalCode: string;
}

/**
 * <pre>
 * B - Bank card
 * I - Invoice
 * C - Cash
 * "-" - none
 * W - Wallet
 * </pre>
 */
export enum OndemandOrderPaymentType {
    TYPE_BANK_CARD = 'B',
    TYPE_INVOICE = 'I',
    TYPE_CASH = 'C',
    TYPE_NONE = '-',
    TYPE_WALLET = 'W'
}
/**
 * <pre>
 * P - processing (Koostamisel)
 * C - confirmed (Kinnitatud)
 * A - cancelled (Tühistatud)
 * D - Done (Täidetud)
 * U - undone (Täitmata)
 * B - On board (Täitmisel)
 * L - Late cancelled by customer (Tühistatud kliendi tõttu)
 * R - cancelled by carrier (Tühistatud vedaja tõttu)
 * </pre>
 */
export enum OndemandOrderStatus {
    PROCESSING = 'P',
    CONFIRMED = 'C',
    CANCELLED = 'A',
    DONE = 'D',
    UNDONE = 'U',
    ONBOARD = 'B',
    CANCELLED_BY_CUSTOMER = 'L',
    CANCELLED_BY_CARRIER = 'R'
}

export const OndemandOrderStatusMap: Record<OndemandOrderStatus, string> = {
    P: 'RIDE_STATUS_PROCESSING',
    C: 'RIDE_STATUS_CONFIRMED',
    A: 'RIDE_STATUS_CANCELLED',
    D: 'RIDE_STATUS_DONE',
    U: 'RIDE_STATUS_UNDONE',
    B: 'RIDE_STATUS_ONBOARD',
    L: 'RIDE_STATUS_CANCELLED_BY_CUSTOMER',
    R: 'RIDE_STATUS_CANCELLED_BY_CARRIER'
};

export const OndemandOrderPaymentTypeMap: Record<OndemandOrderPaymentType, string> = {
    'B': 'RIDE_PAYMENT_TYPE_BANK_CARD',
    'I': 'RIDE_PAYMENT_TYPE_INVOICE',
    'C': 'RIDE_PAYMENT_TYPE_CASH',
    '-': 'RIDE_PAYMENT_TYPE_NONE',
    'W': 'RIDE_PAYMENT_TYPE_WALLET'
};

export interface OndemandRide {
    id: number;

    attendant_count: number;
    changed_by: number;
    changed_by_name: string;
    chb_type: string;
    created_at: string;
    customer_id: number;
    customer_name: string;
    destination_address: string;
    destination_standby_time: number;
    destination_time_min: number;
    destination_timestamp: string;
    discount_issued_id: number;
    web_client_comment?: string;
    duty_id: number;
    invoice_payment: boolean;
    lifter_count: number;
    lifter_drive_along: boolean;
    location: string;
    location_reg_number?: string;
    ondemand_end_stop_id: number;
    ondemand_start_stop_id: number;
    ondemand_trip_id: number;
    order_date: string;
    org_id: number;
    payment_type: OndemandOrderPaymentType[];
    price?: number;
    price_list_exception: false;
    price_with_vat: true;
    priority_time: 'DEPARTURE' | 'ARRIVAL';
    region_id: number;
    seat_type: string;
    starting_point_address: string;
    starting_point_standby_time: number;
    starting_point_time_min: number;
    starting_point_timestamp: string;
    status: OndemandOrderStatus;
    stock_location_id: number;
    updated_at: string;
    vehicle_type: string;
    shopping_cart_payment_type: OndemandOrderPaymentType;
}

export interface OnDemandPublicTrip {
    arrival_timestamp: string;
    departure_timestamp: string;
    shape_polyline: string;
    start_stop_latitude?: number;
    start_stop_longitude?: number;
    end_stop_latitude?: number;
    end_stop_longitude?: number;
    reg_number?: string;
    stops?: {
        address: string;
        latitude: number;
        longitude: number;
        arrival_time?: string;
        arrival_timestamp?: string;
        departure_timestamp?: string;
    }[];
}

export interface LatLng {
    lat: number;
    lng: number;
}

export interface MapPoint extends LatLng {
    address: string;
}

export interface AllowedCardTypes {
    type: string;
    name: string;
    prefix: string;
    displayPrefix: boolean;
    translationTag: string;
    cardNumberLength?: number;
}

export interface HeaderElement {
    id?: string;
    icon?: string;
    path: string;
    label: string;
    type?: HeaderSubItemType;
    condition?: ConditionType[];
    subPaths?: HeaderElement[];
    flags?: string[];
    isExternal?: boolean;
    externalUrl?: string;
}

export enum HeaderSubItemType {
    TEXT = 'text',
    PHONE = 'phone',
    EMAIL = 'email',
    EXTERNAL_LINK = 'externalLink',
    ACCESSIBILITY = 'accessibility',
    LANGUAGE = 'language'
}

export interface MenuItemV2 {
    label: string;
    condition: string[];
    id: string;
    path: string;
    subPaths: MenuItemV2Child[];
    hasDropdown?: boolean;
    menuId?: number;
    dropdownSections?: DropdownSection[];
}

export interface DropdownSection {
    title: string;
    subtitle?: string;
    description?: string;
    path?: string;
    condition?: string[];
    isExternal?: boolean;
    items?: DropdownItem[];
}

export interface DropdownMenuItem {
    id: string;
    label: string;
    path?: string;
    hasDropdown: boolean;
    menuId: number;
    isExternal?: boolean;
    isMobileOnly?: boolean;
    condition: ConditionType[];
    dropdownSections?: DropdownSection[];
}

export interface DropdownItem {
    label: string;
    path: string;
    description?: string;
    notice?: string;
    isExternal?: boolean;
}

export interface DropdownMenuData {
    layout: 'two-column' | 'grid' | 'list' | 'mixed';
    title?: string;
    header?: {
        title: string;
        items?: DropdownItem[];
    };
    sections?: DropdownSection[];
    items?: DropdownItem[];
}

interface MenuItemV2Child {
    path: string;
    label: string;
    icon: string;
    icon_type: string;
    icon_usage: string;
    condition: string[];
}

export enum ConditionType {
    SHOW_BOOK_ARIDE = 'showBookARide',
    AUTH_REQUIRED = 'authRequired',
    ALWAYS = 'always'
}

export enum CardTypes {
    UTN2_CARD = 'Y',
    WALTTI_CARD = 'W',
    QR_CARD = 'Q'
}

export interface CardPersonalizing {
    api_version: number;
    beneficial: boolean | 'Y' | 'N';
    card_id: string;
    id_card: string;
    personal_code: string;
    terms_version: number;
}

export interface IsicCardActivation {
    personal_code: string;
}

export class ProductNotifications {
    notification_mask: number;
    mobile_phone?: string;
    email?: string;
}

export enum NotificationsTypeMask {
    BOUGHT_EMAIL = 1,
    ACTIVATED_EMAIL = 2,
    ENDING_EMAIL = 4,
    BOUGHT_PHONE = 8,
    ACTIVATED_PHONE = 16,
    ENDING_PHONE = 32
}

export class BankLinkPayment {
    id: number;
    created_at: string;
    updated_at: string;
    shopping_cart_id: number;
    status: string;
    amount: number;
    reference: string;
    sender_account: string;
    account_name: string;
    currency: string;
    account_id: number;
    payment_reference: string;
    timestamp: string;
    receipt_no?: string;
    recurring_payment_type?: string;
    nonce?: string;
    swish_app_token?: string;
    payment_error?: string;
}

export interface DeviceKey {
    kid: string;
    did: string;
    kty: string;
    k: string;
    exp: number;
    iat: number;
    ua: number;
}

export interface DeviceKdkResponse {
    keys: DeviceKdk[];
}

export interface DeviceKdk {
    kty: string;
    kid: string;
    k: string;
    kdf: string;
}

export interface DeviceKdkCache {
    kdk: DeviceKdk;
    exp: number;
}

export interface InitiatePaymentRequestBody {
    returnUrl: string;
    abortUrl?: string;
    lang?: string;
}

export interface InitiatePaymentResponse {
    paymentUrl: string;
    ticketIds: string[];
}

export interface PaymentMethodQueryParams {
    posPaymentMethodId?: string;
    cardId?: string;
    rememberPaymentMethod?: boolean;
}

export interface InitiatePaymentRequest {
    shoppingCartId: number;
    paymentId: number | string;
    queryParams?: PaymentMethodQueryParams;
    ticketId?: number;
    bankCardPersonalize?: boolean;
    merchantType?: string;
    customerAccountId?: number | string;
    tripPurpose?: string;
}

export enum SwishTransactionStatus {
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    FAIL = 'FAIL'
}

export enum ValidityType {
    activatedOnFirstValidation = 'TICKET_ACTIVATED_ON_FIRST_VALIDATION',
    productHasStartDate = 'TICKET_START_DATE',
    userSelectedStartDate = 'MANUAL_START_DATE',
    activatedAtPurchase = 'TICKET_ACTIVATED_AT_PURCHASE'
}

export enum AllowedBikeType {
    NO_BIKES_INFORMATION = '0',
    BIKES_ALLOWED = '1',
    NO_BIKES_ALLOWED = '2',
    SOME_BIKES_ALLOWED = '3'
}

export enum RouteClassType {
    TRAM = 'A',
    CITY_PUBLIC = 'C',
    CITY_COMMERCIAL = 'D',
    EXPRESS = 'E',
    FERRY = 'F',
    COUNTY_COMMERCIAL = 'K',
    INTER_CITY_COMMERCIAL = 'L',
    MICRO_BUS = 'M',
    TROLLEY = 'O',
    HIGH_SPEED_ROUTE = 'Q',
    COUNTY_PUBLIC = 'R',
    SPECIAL_ROUTE = 'S',
    TRAIN = 'T'
}

export interface DisruptionMessageResponse {
    packageVersion: string | Date;
    messages: DisruptionMessage[];
}

export interface DisruptionMessage {
    id: number;
    activePeriodStart: string | Date;
    activePeriodEnd: string | Date;
    lastModified?: string | Date;
    created?: string | Date;
    versionNumber?: number;
    informedEntities: DisruptionMessageEntity[];
    headerTextDefault: string;
    effect?: DisruptionMessageEffectType;
    headerText?: DisruptionMessageTranslation[];
    descriptionTextDefault: string;
    descriptionText?: DisruptionMessageTranslation[];
    messageType: DisruptionMessageType;
    alertUrls: Array<string>;
    severity: DisruptionMessageSeverity;
}

export interface ProcessedUrl {
    type: string;
    src: string;
    alt?: string;
}

export interface DisruptionMessagePUBTRANS {
    alertHeaderText: string;
    alertDescriptionText: string;
    alertUrl: null;
    effectiveStartDate: Date;
    effectiveEndDate: Date;
    stops?: Array<DisruptionMessageSTOP>;
}

export interface DisruptionMessageSTOP {
    code: null;
    id: string;
    lat: null;
    locationType: null;
    lon: null;
    name: string;
}

export enum DisruptionMessageSeverity {
    CRITICAL = 'CRITICAL',
    INFO = 'INFO',
    WARNING = 'WARNING'
}

export enum DisruptionMessageType {
    ALERT_BUS = 'ALERT_BUS',
    ALERT_STOP = 'ALERT_STOP',
    ALERT_BUS_AND_STOP = 'ALERT_BUS_AND_STOP',
    ALERT_GENERIC = 'ALERT_GENERIC'
}

export enum TravelMedia {
    card = 'CARD',
    email = 'EMAIL',
    personalCode = 'PERSONAL_CODE',
    bank_card = 'BANK_CARD'
}

export enum DisruptionFilters {
    CATEGORY = 'CATEGORY',
    ROUTE = 'ROUTE',
    DIRECTION = 'DIRECTION',
    UPCOMING = 'UPCOMING',
    DATE = 'DATE'
}

export enum DisruptionMessageEntityType {
    STOP = 'STOP',
    ROUTE = 'ROUTE'
}

export interface DisruptionMessageEntity {
    stopId?: string;
    stopName?: string;
    designation: string;
    routeId?: number;
    routeType: string;
    routeLongName?: string;
    routeShortName: string;
    icon?: DisruptionMessageEntityIcon;
    tripDirection: number;
    tripHeadsign?: string;
    tripId?: string;
    activePeriodStart?: string;
    activePeriodEnd?: string;
    type?: DisruptionMessageEntityType | null;
}
export interface DisruptionMessageRequest {
    stopIds?: string[];
    routeIds?: string[];
    tripHeadsign?: string[];
    dateTime?: number; // time in seconds since unix epoch
}

export interface DisruptionMessageEntityIcon {
    color?: string;
    shape?: 'ROUNDED' | 'SQUARED' | 'RECTANGLE';
}

export enum DisruptionMessageEffectType {
    NO_SERVICE = 'NO_SERVICE',
    REDUCED_SERVICE = 'REDUCED_SERVICE',
    SIGNIFICANT_DELAYS = 'SIGNIFICANT_DELAYS',
    DETOUR = 'DETOUR',
    ADDITIONAL_SERVICE = 'ADDITIONAL_SERVICE',
    MODIFIED_SERVICE = 'MODIFIED_SERVICE',
    OTHER_EFFECT = 'OTHER_EFFECT',
    UNKNOWN_EFFECT = 'UNKNOWN_EFFECT',
    STOP_MOVED = 'STOP_MOVED'
}

export interface DisruptionMessageTranslation {
    language: string;
    text: string;
}

export class DisruptionMessagesSummary {
    amount: number;
    icon: string;
    tag: string;
    path?: string;

    constructor(amount: number, icon: string, tag: string, path?: string) {
        this.amount = amount;
        this.icon = icon;
        this.tag = tag;
        this.path = path;
    }
}

export interface DepartureArrivalTime {
    datetime: string;
    type?: 'leaveAfter' | 'arriveBy';
}

export class PaymentGatewayIntent {
    banklinkAccountId: number;
    code: string;
    paymentId: number;
    shoppingCartId: number;
    lang: string;
    returnUrl: string;
    abortUrl?: string;
    authToken: string;
}

export enum PaymentGatewayMethods {
    VPO = 'vpo',
    LEGACY = 'legacy'
}

export class ViispTicketResponse {
    ticketId: string;
    url: string;
}
export class ViispTokenResponse {
    token: string;
}

export interface Locales {
    locales: string[];
    prefix: string;
}
export interface AlertUrls {
    src: string;
    alt?: string;
    type: 'image' | 'url';
}

export interface MessageLines {
    line: string;
    icon?: DisruptionMessageEntityIcon;
}

export enum CardType {
    TRAVEL_CARD = 'TRAVEL_CARD',
    BANK_CARD = 'BANK_CARD'
}

export enum TicketProductType {
    DISCOUNT = 'S',
    MIXED = 'Q'
}

export interface AddCardButton {
    amount?: number;
    disabled: boolean;
}

export interface TransferTicketEmailReceiverVerify {
    email: string;
}

export interface TicketProductProperty {
    code: string;
    value?: string;
}

// Capacitor emitted platforms enum
export enum Platform {
    WEB = 'web',
    ANDROID = 'android',
    IOS = 'ios'
}
export enum AccessType {
    Admin = 'A',
    ParentAdmin = 'PA'
}

export interface ExternalLinksConfig {
    'map'?: string;
    'buy-ticket'?: string;
    'add-money'?: string;
    'bike-garage'?: string;
    'my-account'?: string;
    'book-a-ride'?: string;
    'bankcard-tickets'?: string;
    'personalize'?: string;
    'ondemand-trips'?: string;
    'explore'?: string;
}

export interface StopRouteResult {
    id: string;
    type: string;
    name: string;
    desc?: string;
    shortName?: string;
    code?: string;
    mode?: any;
    color?: string;
}

export interface AddNotificationsViewModel {
    results: {
        stopRoutes: StopRouteResult[];
        addresses: any[];
        poi: any[];
    };
    searchText: string;
    isLoading: boolean;
}
export interface DisturbanceSubscription {
    id?: string;
    stopId: string | null;
    routeId: string | null;
    daysOfWeek: string[];
    startedAt: string;
    endedAt: string | null;
    active: boolean;
}

export enum NotificationPreferenceChannel {
    Email = 'EMAIL',
    SMS = 'SMS',
    Push = 'PUSH_NOTIFICATION'
}

export interface NotificationPreferences {
    channel: NotificationPreferenceChannel;
    enabled: boolean;
    settings: {
        GENERAL: boolean;
        DISTURBANCE: boolean;
        RECEIVE_RECEIPT: boolean;
    };
}

export interface UserNotificationPreferences {
    customerAccountId?: string;
    preferences: NotificationPreferences[];
}

export enum ShapeType {
    ROUNDED = 'ROUNDED',
    SQUARED = 'SQUARED',
    RECTANGLE = 'RECTANGLE'
}

export enum SubscriptionType {
    Route = 'route',
    Stop = 'stop'
}

export interface SubscriptionItemType {
    id: string;
    type: 'route' | 'stop';
    name: string;
    isSubscribed: boolean;
    label?: string;
    icon: {
        color?: string;
        shape: ShapeType;
    };
    originalData: any;
}

export enum NotificationSettingType {
    GENERAL = 'GENERAL',
    DISTURBANCE = 'DISTURBANCE',
    RECEIVE_RECEIPT = 'RECEIVE_RECEIPT'
}

export enum NotificationTabType {
    ROUTES = 'ROUTES',
    STOPS = 'STOPS',
    ROUTE_GROUPS = 'ROUTE_GROUPS'
}

export interface ValidityTimerData {
    remainingTimeText: string;
    progressValue: number;
    isExpired: boolean;
}

export interface TicketPurchaseTimeModalData {
    type: 'warning' | 'error';
    message: string;
    departureTime?: string;
}

export type WithEndDate =
    | { ending: string | Date; endDate?: string | Date }
    | { endDate: string | Date; ending?: string | Date };

export type TicketWithProductInfo = WithEndDate & {
    starting?: string | Date;
    productId?: string | number;
    product_id?: number;
    product?: Product;
    product_object?: Product;
    product_type?: string;
};

/** WMB public-api-service DTO: com.ridango.wmb.publicapiservice.common.dto.ApiErrorResponse */
export interface WmbApiErrorResponse {
    timestamp: string;
    status: number;
    error: string;
    message: string;
    code?: string | null;
    path: string;
}

export type ApiRouterErrorResponse = string;

export type TicketActionErrorResponse = WmbApiErrorResponse | ApiRouterErrorResponse;

export type TicketActionHttpErrorResponse = Omit<HttpErrorResponse, 'error'> & {
    error: TicketActionErrorResponse;
};
